import React from 'react';
import { ArrowLeft } from 'lucide-react';

interface Props {
  onBack: () => void;
}

const PrivacyPolicy: React.FC<Props> = ({ onBack }) => {
  return (
    <div className="max-w-3xl mx-auto p-6 bg-white border-2 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,0.1)] my-8 animate-in fade-in slide-in-from-bottom-4">
      <button 
        onClick={onBack}
        className="flex items-center gap-2 text-gray-500 hover:text-black font-hand mb-6 transition-colors"
      >
        <ArrowLeft size={20} />
        Back to Sketching
      </button>

      <h1 className="text-3xl font-bold font-hand mb-6">Privacy Policy</h1>
      <p className="font-hand text-gray-500 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

      <div className="prose font-sans text-gray-800 space-y-6">
        <section>
          <h2 className="text-xl font-bold mb-2">1. Introduction</h2>
          <p>
            Welcome to LogiSketch ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. 
            This privacy policy will inform you as to how we look after your personal data when you visit our website and tell you 
            about your privacy rights and how the law protects you.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2">2. Data We Collect</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Identity Data:</strong> Includes email address and user ID (via Google/Supabase Auth).</li>
            <li><strong>Usage Data:</strong> Includes text prompts you enter and the images generated.</li>
            <li><strong>Transaction Data:</strong> Details about payments (processed securely via Stripe). We do not store full credit card numbers.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2">3. How We Use Your Data</h2>
          <p>We use your data to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Provide and manage your account.</li>
            <li>Process your credits and payments.</li>
            <li>Generate images using third-party AI providers (Google Gemini & Imagen).</li>
            <li>Improve our service functionality.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2">4. Third-Party Services</h2>
          <p>We share necessary data with the following trusted partners to operate our service:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Supabase:</strong> For authentication and database hosting.</li>
            <li><strong>Stripe:</strong> For payment processing.</li>
            <li><strong>Google Cloud (Vertex AI/Gemini):</strong> For generating text and image content.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2">5. Data Security</h2>
          <p>
            We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used, or accessed in an unauthorized way. 
            Access to your personal data is limited to employees, agents, contractors, and other third parties who have a business need to know.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2">6. Contact Us</h2>
          <p>
            If you have any questions about this privacy policy or our privacy practices, please contact us at: 
            <a href="mailto:support@logisketch.com" className="text-blue-600 hover:underline ml-1">support@logisketch.com</a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;